define((require) => {
    "use strict";

    const LoggedIn = require("shared/security/LoggedIn");
    const Class    = require("shared/util/Class");
    const Enum     = require("shared/util/Enum");

    /**
     * Returns true if the supplied set of roles/role names or privileges/privilege names are the same,
     * false otherwise. The test is done on instances, secondarily on names only, case-sensitive.
     *
     * @param {Role|Privilege}        scope   The role or privilege to test against a supplied role, privilege, or name;
     *                                        never null.
     * @param {Role|Privilege|String} [other] The role, privilege, or name to test against <tt>scope</tt>; a falsey value
     *                                        will cause false to be returned.
     * @return {Boolean} True if the same role/privilege, false otherwise.
     */
    function is(scope, other) {
        if (scope === other) {
            return true;
        }

        if (typeof other === "string") {
            return other === scope.name;
        } else if (other) {
            return other.name === scope.name;
        }

        return false;
    }

    /**
     * Tests the required array of privileges against the granted privileges, and returns true
     * if all privileges in <tt>required</tt> (and maybe more) are contained in the granted array
     * of privileges, false otherwise.
     *
     * @param  {Privilege[]} required The required privileges; never null, but can be empty.
     * @param  {Privilege[]} granted  The granted privileges; never null, but can be empty.
     * @return {Boolean} True if <tt>granted</tt> contains at least all the privileges in <tt>required</tt>,
     *                   false otherwise.
     */
    function has(required, granted) {
        // No required privileges, "object" has access...
        if (!required.length) {
            return true;
        }
        // Find the privileges granted to "object": "required" must contain all of these for this function to
        // return true, but more may be contained...
        const n = required.length;
        const m = granted.length;

        let privilege;
        let i;
        let j;

        // If more privileges are required than granted, we can return false immediately...
        if (n > m) {
            return false;
        }
        // Test each required privilege...
        loop:
        for (i = 0; i < n; i++) {
            privilege = required[i];
            // Against each granted one; this is O(n2), but we operate with few privileges, so it will suffice for now...
            for (j = 0; j < m; j++) {
                if (privilege === granted[j]) {
                    continue loop;
                }
            }
            return false;
        }
        return true;
    }

    /**
     * Returns the role granted to the supplied object, if any. <p>
     *
     * The granted role is deduced as: <ol>
     *
     * <li> If <tt>object</tt> is falsey, the role of the currently logged in principal is returned, if any.
     * <li> If <tt>object</tt> is otherwise falsey, null is returned.
     * <li> If <tt>object</tt> is a role, the role is returned.
     * <li> If <tt>object</tt> is a string, the role with said name is returned.
     * <li> If <tt>object</tt> is an object declaring a raw "role" property, this function is called
     *      recursively with the value of said property as "object".
     * <li> If <tt>object</tt> is a Backbone model declaring a "role" property, this function is called
     *      recursively with the value of said property as "object".
     * </ol>
     *
     * @param  {Principal|Role|String|*} object     The object to get the role for; can be falsey.
     * @param  {Boolean}                [recursive] True when called recursively, false otherwise.
     * @return {Role} The role; can be null.
     */
    function getGrantedRole(object, recursive) {
        // Falsey, e.g. when a principal has not logged in...
        if (object) {
            // Role...
            if (object instanceof Role) {
                return object;
            // Role name, not principal name...
            } else if (typeof object === "string") {
                return Roles[object] || new Role(object); // Create new role, but with no privileges!
            // Recurse for raw "role" property...
            } else if (object.role) {
                return getGrantedRole(object.role, true);
            // Recurse for Backbone model "role" property; this will also catch Principal!...
            } else if (typeof object.get === "function") {
                return getGrantedRole(object.get("role"), true);
            }
        } else if (!recursive) {
            return getGrantedRole(LoggedIn.principal(), true);
        }
        return null;
    }

    /**
     * Returns the privileges granted to the supplied object, if any. <p>
     *
     * The granted privileges are deduced as: <ol>
     *
     * <li> If <tt>object</tt> is falsey, the privileges of the currently logged in principal are returned, if any.
     * <li> If <tt>object</tt> is otherwise falsey, an empty array is returned.
     * <li> If <tt>object</tt> is a role, the privileges of the role are returned.
     * <li> If <tt>object</tt> is a privilege, an array with only said privilege is returned.
     * <li> If <tt>object</tt> is a string, the privileges of the role with said name are returned.
     * <li> If <tt>object</tt> is an object declaring a raw "role" property, this function is called
     *      recursively with the value of said property as "object".
     * <li> If <tt>object</tt> is a Backbone model declaring a "role" property, this function is called
     *      recursively with the value of said property as "object".
     * </ol>
     *
     * @param  {Principal|Role|Privilege|String|*} object The object to get the privileges for; can be falsey.
     * @return {Privilege[]} The array of privileges; never null, but can be empty.
     */
    function getGrantedPrivileges(object) {
        if (object instanceof Privilege) {
            return [object];
        }
        const role = getGrantedRole(object);
        return role === null ? [] : role._privileges;
    }

    /**
     * @private
     * @constructor
     * A <i>role</i> describes a potentially empty set of privileges assigned to a guarded entity,
     * like a principal. <p>
     *
     * A role can also be assigned to other type of guarded entities, but access enforcement is always done
     * with respect to a principal. A user, for example, also has a role describing the role assigned to the user,
     * but a user will never be checked for the role, only via the principal logged in on behalf of the user. <p>
     *
     * To test if the current logged in principal, if any, has <b>at least</b> all the privileges of a given role,
     * simply do:
     * <pre>
     *    if (Permissions.Roles.MANAGER.can()) {
     *        // principal has all the privileges of the manager role
     *    }
     *    ..
     *    if (Permissions.Roles.PUBLISHER.cannot()) {
     *        // principal does not have all the privileges of the publisher role
     *    }
     * </pre>
     *
     * In addition, any entity model declaring a raw or Backbone <tt>role</tt> property storing a role name or
     * an actual role, a role name, a role, or a privilege can all be tested against this role in the same
     * way, like:
     * <pre>
     *    var entity = {role: "Manager", ..};
     *    if (Permissions.Roles.PUBLISHER.can(entity)) {
     *        // "entity" has at least the all the privileges of the publisher role since manager
     *    }
     *    ..
     *    var user = new User(..); // e.g. user.get("role")
     *    if (Permissions.Roles.CREATOR.cannot(user)) {
     *        // "user" does not have the all the privileges of the creator role
     *    }
     *    ..
     *    var privilege = Permissions.Privileges.Publish;
     *    if (Permissions.Roles.ADMIN.can(privilege)) {
     *        // true
     *    }
     *    if (Permissions.Roles.ADMIN.cannot(privilege)) {
     *        // false
     *    }
     *    ..
     * </pre>
     *
     * All roles are exposed under <tt>Permissions.Roles.all()</tt>.
     *
     * @param {String}       name        The name of this role; never null.
     * @param {Privilege[]} [privileges] The privileges associated with this role; can be null, i.e. none. Immutable,
     *                                   never changes once assigned.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    const Role = Class.extend({

        /**
         * @type {String}
         * The read-only name of this role; never null.
         */
        name: null,

        /**
         * @private
         * @type {Privilege[]}
         * The privileges associated with this role; never null, but can be empty. No duplicates.
         */
        _privileges: null,

        constructor(name, privileges) {
            this.name = name;
            this._privileges = privileges || [];
        },

        /**
         * Returns true if this role is the same role as the role granted to the supplied object, and thus
         * can perform the action(s) guarded by it, false otherwise. <p>
         *
         * The comparison is done on role name only, case-sensitive.
         *
         * @param  {Principal|Role|String|*} [object] The role or object granted to the role to test; will default to
         *                                            the role of the currently logged in principal, if any. A string
         *                                            means a role name.
         * @return {Boolean} True if the same role, false if it is not.
         * @see    #not
         */
        is(object) {
            return is(this, getGrantedRole(object));
        },

        /**
         * Returns true if this role is not the same role as the role granted to the supplied object, and thus
         * cannot perform the exact action(s) guarded by it, false if it is. <p>
         *
         * The comparison is done on role name only, case-sensitive.
         *
         * @param  {Principal|Role|String|*} [object] The role or object granted to the role to test; will default to the
         *                                            role of the currently logged in principal, if any. A string means
         *                                            a role name.
         * @return {Boolean} True if not the same role, false if it is.
         * @see    #is
         */
        not(object) {
            return !this.is(object);
        },

        /**
         * Returns true if the supplied object has at least all the privileges granted to this role, and can
         * thus perform the action(s) guarded by this role, false otherwise
         *
         * @param  {Principal|Role|Privilege|String|*} [object] The object to test; will default to the currently
         *                                                      logged in principal, if any. A string means a role name.
         * @return {Boolean} True if the (defaulted) object has at least all the privileges granted to this role,
         *                   false otherwise.
         * @see    #cannot
         */
        can(object) {
            return has(this._privileges, getGrantedPrivileges(object));
        },

        /**
         * Returns true if the supplied object does not have all the privileges granted to this role, and thus cannot
         * perform the action(s) guarded by this role, false otherwise
         *
         * @param  {Principal|Role|Privilege|String|*} [object] The object to test; will default to the currently logged
         *                                                      in principal, if any. A string means a role name.
         * @return {Boolean} True if the (defaulted) object does not have all the privileges granted to this role,
         *                   false otherwise.
         * @see    #can
         */
        cannot(object) {
            return !this.can(object);
        },

        /**
         * Returns the string representation of this role.
         *
         * @return {String} The string representation; never null.
         */
        toString() {
            return this.name;
        }
    });

    /**
     * @private
     * @constructor
     * A <i>privilege</i> describes a given permission assigned to a role. <p>
     *
     * To test if the current logged in principal, if any, has a given privilege, simply do:
     * <pre>
     *    if (Permissions.Privilege.PUBLISH.can()) {
     *        // principal has the privilege to publish
     *    }
     *    ..
     *    if (Permissions.Privilege.CREATE.cannot()) {
     *        // principal does not have the privilege to create
     *    }
     * </pre>
     *
     * In addition, any entity model declaring a raw or Backbone <tt>role</tt> property storing a role name or
     * an actual role, a role name, a role, or a privilege can all be tested against this privilege in the same
     * way, like:
     * <pre>
     *    var entity = {role: "Manager", ..};
     *    if (Permissions.Privilege.PUBLISH.can(entity)) {
     *        // "entity" has the privilege to publish
     *    }
     *    ..
     *    var user = new User(..); // e.g. user.get("role")
     *    if (Permissions.Privilege.CREATE.cannot(user)) {
     *        // "user" does not have the privilege to create
     *    }
     *    ..
     *    var role = Permissions.Roles.PUBLISHER;
     *    if (Permissions.Privilege.ADMIN.can(role)) {
     *        // always false
     *    }
     *    if (Permissions.Privilege.ADMIN.cannot(role)) {
     *        // always true
     *    }
     * </pre>
     *
     * All privileges are exposed under <tt>Permissions.Privileges.all()</tt>.
     *
     * @param {String} name The name of this privilege; never null.
     */
    const Privilege = Class.extend({

        /**
         * @type {String}
         * The read-only case-sensitive name of this privilege; never null.
         */
        name: null,

        constructor(name) {
            this.name = name;
        },

        /**
         * Returns true if the supplied privilege or privilege name is the same as this privilege,
         * false if it is not. <p>
         *
         * The comparison is case-sensitive.
         *
         * @param {String|Privilege} privilege The privilege or privilege name to test against this privilege;
         *                                     a falsey value will cause false to be returned.
         * @return {Boolean} True if the same privilege, false if it is not.
         * @see    #not
         */
        is(privilege) {
            return is(this, privilege);
        },

        /**
         * Returns true if the supplied privilege or privilege name is not the same as this privilege,
         * false if it is. <p>
         *
         * The comparison is case-sensitive.
         *
         * @param {String|Privilege} privilege The privilege or privilege name to test against this privilege;
         *                                     a falsey value will cause true to be returned.
         * @return {Boolean} True if not the same privilege, false if it is.
         * @see    #is
         */
        not(privilege) {
            return !this.is(privilege);
        },

        /**
         * Returns true if the supplied object has this privilege and can perform the action guarded by it,
         * false otherwise.
         *
         * @param  {Principal|Role|Privilege|String|*} [object] The object to test; will default to the currently logged
         *                                                      in principal, if any. A string means a role name.
         * @return {Boolean} True if (the defaulted) object has this privilege, false otherwise.
         * @see    #cannot
         */
        can(object) {
            // The required privileges are just this privilege, and the granted ones those from the object...
            return has([this], getGrantedPrivileges(object));
        },

        /**
         * Returns true if the supplied object does not have this privilege and cannot perform the action guarded by it,
         * false otherwise.
         *
         * @param  {Principal|Role|Privilege|String|*} [object] The object to test; will default to the currently logged
         *                                                      in principal, if any. A string means a role name.
         * @return {Boolean} True if (the defaulted) object does not have this privilege, false otherwise.
         * @see    #can
         */
        cannot(object) {
            return !this.can(object);
        },

        /**
         * Returns the string representation of this privilege.
         *
         * @return {String} The string representation; never null.
         */
        toString() {
            return this.name;
        }
    });

    /**
     * @private
     * @type {*}
     * The supported privileges, indexed by their actual name. <p>
     *
     * Each role with have an associated array of privileges.
     */
    const Privileges = {
        Create:       new Privilege("Create"),
        Publish:      new Privilege("Publish"),
        Admin:        new Privilege("Admin"),
        SiteAdmin:    new Privilege("SiteAdmin"),
        Unobtainable: new Privilege("Unobtainable") // not assigned to any principal, so testing against this privilege will always fail!
    };

    /**
     * @private
     * @type {*}
     * All supported roles, where associated privileges are indexed by the actual role name for fast lookup. <p>
     *
     * At principal creation time, any role not defined here, will be assigned to the principal, but without any
     * associated privileges!
     */
    const Roles = {
        Spectator: new Role("Spectator"),
        Creator:   new Role("Creator",   [Privileges.Create]),
        Publisher: new Role("Publisher", [Privileges.Create, Privileges.Publish]),
        Manager:   new Role("Manager",   [Privileges.Create, Privileges.Publish, Privileges.Admin]),
        SiteManager:  new Role("SiteManager",   [Privileges.Create, Privileges.Publish, Privileges.Admin, Privileges.SiteAdmin])
    };

    /**
     * @type {*}
     * All supported roles and privileges as exposed by this module.
     */
    const Permissions = {

        /**
         * Defines all privileges.
         */
        Privileges: Enum.create("Privilege", "name", {

            CREATE:       Privileges.Create,
            PUBLISH:      Privileges.Publish,
            ADMIN:        Privileges.Admin,
            SITEADMIN:    Privileges.SiteAdmin,
            UNOBTAINABLE: Privileges.Unobtainable

        },  {

            /**
             * Returns the privilege identified by the supplied privilege or name, or null if unknown.
             *
             * @param {String|Privilege} privilege The privilege name or privilege to find; a falsey value will return null.
             * @return {Privilege} The corresponding privilege; or null if not found.
             */
            of(privilege) {
                if (privilege instanceof Privilege) {
                    return privilege;
                } else if (!privilege) {
                    return null;
                }
                return Privileges[privilege] || null;
            }
        }),

        /**
         * Defines all known roles. <p>
         *
         * At runtime, new roles may be created based on an unknown role name, but if so, will always be associated
         * with an empty array of privileges.
         */
        Roles: Enum.create("Role", "name", {

            SPECTATOR: Roles.Spectator,
            CREATOR:   Roles.Creator,
            PUBLISHER: Roles.Publisher,
            MANAGER:   Roles.Manager,
            SITEMANAGER: Roles.SiteManager

        }, {

            /**
             * Returns the role granted to the supplied object, if any, defaulting to Spectator. <p>
             *
             * The role is deduced as: <ol>
             *
             * <li> If <tt>object</tt> is falsey, the Spectator role is returned.
             * <li> If <tt>object</tt> is a role, the role is returned.
             * <li> If <tt>object</tt> is a string, the role with said name is returned. If no such role exist, a
             *      new role is created with the name, but <b>no</b> privileges.
             * <li> If <tt>object</tt> is an object declaring a raw "role" property, this function is called
             *      recursively with the value of said property as "object".
             * <li> If <tt>object</tt> is a Backbone model declaring a "role" property, this function is called
             *      recursively with the value of said property as "object".
             * </ol>
             *
             * @param  {Principal|Role|String|*} object The object to get the role for; can be falsey, in which
             *                                          case Spectator is returned.
             * @return {Role} The corresponding role; never null.
             */
            of(object) {
                // true argument: Do *not* default to the logged in principal here...
                return getGrantedRole(object, true) || Roles.Spectator;
            },

            can(role, object) {
                return Permissions.Roles.of(role).can(object);
            },

            cannot(role, object) {
                return Permissions.Roles.of(role).cannot(object);
            }

        })
    };

    return Permissions;
});



// WEBPACK FOOTER //
// ./src/shared/security/Permissions.js